<template>
  <div
    class="fu-queue"
    :class="[`fu-queue--${layout}`, `fu-queue--mode-${mode}`, { 'fu-queue--embedded': embedded }]"
    data-test="fu-queue"
    :data-layout="layout"
    :data-queue="queue"
  >
    <div class="fu-queue__card">
      <div v-if="showHead" class="fu-queue__head">
        <div v-if="showChips" class="fu-queue__chips-row">
          <followups-view-chips :views="viewChips" :value="currentView" :layout="layout" @input="setView" />
          <div v-if="$slots.switches" class="fu-queue__switches">
            <slot name="switches" />
          </div>
        </div>
        <followups-toolbar
          :class="{ 'fu-queue__toolbar--spaced': showChips }"
          :search="search"
          :placeholder="searchPlaceholder"
          :statuses="statuses"
          :status-options="statusOptions"
          :layout="layout"
          :sort-options="sortOptions"
          :sort-key="sort ? sort.key : ''"
          :any-filter="anyFilter"
          @update:search="setSearch"
          @update:statuses="setStatuses"
          @update:sort="setSortKey"
          @clear="clearFilters"
        >
          <template v-if="$slots.filters" #filters>
            <slot name="filters" />
          </template>
          <template v-if="$slots['filters-end']" #filters-end>
            <slot name="filters-end" />
          </template>
        </followups-toolbar>
      </div>

      <followups-table
        :columns="columns"
        :items="shownItems"
        :total="visibleItems.length"
        :layout="layout"
        :sort="sort"
        :selectable="selectable"
        :selected-ids="selectedIds"
        :all-checked="allChecked"
        :some-checked="someChecked"
        :busy="busy"
        :noun="noun"
        :has-more="hasMore"
        :filtered="rows.length > 0"
        :today="today"
        :date-format="dateFormatStr"
        :opening-ids="actions.state.opening"
        :error-ids="actions.state.errors"
        :ai-calling-ids="actions.state.aiCalling"
        :show-ai="showAi"
        :show-list-badge="mode !== 'list'"
        :popover-row-id="popover ? popover.id : null"
        :show-foot="!isProfile"
        @sort="toggleSort"
        @toggle-row="onToggleRow"
        @toggle-all="onToggleAll"
        @open-drawer="openDrawer"
        @open-followup="openPopover"
        @ai-call="item => actions.aiCall(item.row)"
        @remove="item => $emit('remove-patient-from-list', item.row)"
        @show-more="limit += pageSize"
      >
        <template v-if="$scopedSlots.empty" #empty="scope">
          <slot name="empty" v-bind="scope" />
        </template>
      </followups-table>
    </div>

    <followups-bulk-bar
      v-if="showBulkBar"
      :count="selected.length"
      :layout="layout"
      @add="$emit('add-to-existing-list', selected)"
      @create="$emit('create-new-list', selected)"
      @clear="$emit('clear-selection')"
    />

    <followup-popover
      v-if="popoverItem"
      :key="popover.id"
      :name="popoverItem.name"
      :eyebrow="[popoverQueue.label, popoverItem.name].filter(Boolean).join(' · ')"
      :fu="popoverItem.fu"
      :attempts="popoverItem.attempts.count"
      :status-label="popoverItem.statusLabel"
      :today="today"
      :date-format="dateFormatStr"
      :anchor="popover.rect"
      :layout="layout"
      :show-assign="!!popoverQueue.showsAssignee"
      :employees="popoverEmployees"
      :initial-employee-id="popoverEmployeeId"
      :note-placeholder="notePlaceholder"
      @save="onPopoverSave"
      @remove-initial="runDanger('removeInitial')"
      @stop="runDanger('stopFollowups')"
      @restart="runDanger('restartFollowups')"
      @close="closePopover"
    />
  </div>
</template>

<script>
/**
 * FollowupsQueue — the shared follow-ups table, AdminTable.vue's successor (redesign Phase 1A).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 *   <followups-queue :rows="overdueRecallData" queue="overdueRecall" :selected="selectedPatients"
 *     @toggle-row="row => updateSelectedPatients(selectedPatients, row)"
 *     @toggle-all="({ rows, checked }) => checked ? chooseAll(rows, selectedPatients) : chooseNone(selectedPatients)"
 *     @add-to-existing-list="addToExistingList" @create-new-list="createNewList"
 *     @clear-selection="clearSelection" @call-goals-updated="callGoalsUpdated" />
 *
 * Page chrome hooks (Phase 1B): `:view.sync` lets the page set the view chip (the "Due now" card);
 * `@counts` hands the page the chip counts plus { dueToday, pastDue } for its summary card and tab
 * badge; slots `switches` (right of the chips), `filters` (after search) and `filters-end` (after
 * Status) take the page's own toggles and filter buttons. Phase 4: `:external-filter` says the page
 * has narrowed `rows` itself (a bucket / period card), so "Clear" shows and also emits `clear-filters`.
 *
 * The page keeps owning its data, its filters, its lists and its selection; this owns only local
 * UI state (view chip, search, status filter, sort, how many rows are shown, the open popover) and
 * the follow-up saves (followupActions.js — same store actions and payloads as AdminTable).
 *
 * ⛔ Never reads $route. Legacy decided "Admin page" / "Aging page" from the URL
 *    (AdminTable.vue:1238) — exactly what breaks once this renders under /app/schedule. `queue`
 *    and `mode` say everything the URL used to.
 * ⛔ Never mounts a <slideout-panel>: the PAGE owns the single outlet ($showPanel broadcasts to
 *    every mounted one — cypress/node-tests/slideout-outlet-guard.test.js).
 * ⛔ No props named patNum / siteID / loading: src/mixin.js computeds would silently replace them
 *    (cypress/node-tests/mixin-prop-shadow-guardrail.test.js).
 *
 * Where the two legacy-only follow-up states land among the view chips (filterSort.js):
 *   nodate   (no date, but not "Not Set" either) → "Not set" — there is no date to work from.
 *   resolved (Appointment Scheduled / Claim Received) → "Stopped or done" — nothing left to do.
 * A row that is saving keeps the chip and sort position of the state it had before the save.
 *
 * Layout comes from this component's OWN width (ResizeObserver on the root, window resize when
 * there is none), on the export's breakpoints: phone < 640 (cards, no header), tablet < 900
 * (the tablet column set), desktop.
 *
 * mode="profile" (Phase 5) — ONE patient's follow-ups across every queue (Patient Profile; later
 * the Schedule View Patient Panel):
 *   <followups-queue mode="profile" queue="patientProfile" :rows="upcomingFollowups"
 *     :profile-patient-id="patientID" :patient-name="…" :profile-facts="…" @counts="…" />
 * Each row is its OWN queue (queueByTaskTypeID): its follow-up rules (resolveProfileFollowupState
 * — Patient Profile's chain, not AdminTable's), its popover (Assign to only on aging rows, with
 * that queue's responsible employees), its saves and its drawer query (followupActions.js).
 * Columns: queue / detail / status / attempts / follow-up. No chips, search, status filter, sort
 * select, selection, bulk bar, lists, AI call or footer: a patient has at most one row per queue,
 * and every one of those is there to narrow down a long list.
 * `max-rows` + `embedded` let a host draw the first few rows inside a card of its own (Patient
 * Profile's Overview) — same rows, drawer and popover as the Follow-ups tab.
 */
import moment from 'moment-timezone';
import mixin from '../../mixin.js';
import {
  queueByKey, queueByTaskTypeID, rowIdOf, profileRowIdOf, columnsFor, layoutFor, COLUMN_DEFS, PROFILE_QUEUE,
} from '@/utils/followups/queues.js';
import { resolveFollowupState, resolveProfileFollowupState } from '@/utils/followups/followupState.js';
import { statusCategory } from '@/utils/followups/statusCategory.js';
import { buildRow, buildProfileRow } from '@/utils/followups/rowModel.js';
import { parseReminderSettings, reminderCell } from '@/utils/followups/reminderHint.js';
import { VIEWS, countsByView, visibleRows } from '@/utils/followups/filterSort.js';
import { createFollowupActions } from './followupActions.js';
import FollowupsViewChips from './FollowupsViewChips.vue';
import FollowupsToolbar from './FollowupsToolbar.vue';
import FollowupsTable from './FollowupsTable.vue';
import FollowupsBulkBar from './FollowupsBulkBar.vue';
import FollowupPopover from './FollowupPopover.vue';

const PAGE_SIZE = 50;

/** "Showing 50 of 212 <noun>". */
const NOUNS = {
  overdueRecall: 'patients',
  unscheduledTreatment: 'patients',
  outstandingClaims: 'claims',
  outstandingPreAuths: 'pre-authorizations',
  processedPreAuths: 'pre-authorizations',
  patientBalances: 'guarantors',
  brokenAppointments: 'broken appointments',
  patientProfile: 'follow-ups',
};

/** The export's search placeholders, per queue. */
const SEARCH_PLACEHOLDERS = {
  overdueRecall: 'Search patient, carrier, hygienist…',
  unscheduledTreatment: 'Search patient or carrier…',
  outstandingClaims: 'Search patient or carrier…',
  outstandingPreAuths: 'Search patient or carrier…',
  processedPreAuths: 'Search patient or carrier…',
  patientBalances: 'Search guarantor…',
  brokenAppointments: 'Search patient or code…',
};

/** Numeric columns sort largest-first when picked from the phone's Sort select (the export's rule). */
const DESC_FIRST = ['txamt', 'claimfee', 'balance', 'fee', 'att', 'rem'];

export default {
  name: 'FollowupsQueue',
  components: { FollowupsViewChips, FollowupsToolbar, FollowupsTable, FollowupsBulkBar, FollowupPopover },
  mixins: [mixin],
  props: {
    rows: { type: Array, default: () => [] },
    queue: { type: String, required: true },
    mode: {
      type: String,
      default: 'page',
      validator: v => ['page', 'list', 'dashboard', 'profile'].indexOf(v) !== -1,
    },
    // The page's selectedPatients (same array AdminTable received). Never mutated here — the page
    // handles toggle-row / toggle-all with its mixin helpers, as it does today.
    selected: { type: Array, default: () => [] },
    // null ⇒ $can('update', 'followupDate'), exactly what the legacy cell checks.
    canEdit: { type: Boolean, default: null },
    busy: { type: Boolean, default: false },
    // The saved list being shown (list pages' extra.listID) — sent as BelongsToListID when a first
    // follow-up date is set from a list page.
    listId: { type: [Number, String], default: undefined },
    // The view chip, when the page wants a say in it (`:view.sync`) — e.g. the "Due now" summary
    // card's "Show these". Unset ⇒ the queue keeps its own, starting on "All".
    view: { type: String, default: null },
    // The page narrows `rows` itself (Aging's bucket cards, Broken's period cards — Phase 4): true
    // while it does, so the toolbar's "Clear" shows; Clear then also emits `clear-filters` for the
    // page to let go of its own filter. Unset ⇒ exactly the behaviour before.
    externalFilter: { type: Boolean, default: false },
    // mode="profile" only (Phase 5). The patient's "<siteID>-<PatNum>" — the profile rows carry no
    // PatNum, and every save and the drawer lock key on it (Patient Profile sends its `patientID`).
    profilePatientId: { type: String, default: null },
    // The patient's (already-masked) "Last, First", for the popover's wording. Optional.
    patientName: { type: String, default: '' },
    // rowModel.js profileFactsFrom(…) — what the host already knows about the patient, for the
    // Details column. Optional: without it Details shows only what the row itself carries.
    profileFacts: { type: Object, default: null },
    // Draw only the first N rows (0 = no cap). The chip counts and `counts` still cover every row,
    // so a host that caps (Patient Profile's Overview card) keeps its own "N open · M past due".
    maxRows: { type: Number, default: 0 },
    // The queue sits inside a card the host already draws: drop its own card border, radius,
    // shadow and background so it is not a card inside a card.
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      currentView: this.view || 'all',
      search: '',
      statuses: [],
      sort: null,
      limit: PAGE_SIZE,
      pageSize: PAGE_SIZE,
      containerWidth: 1280,
      popover: null,
      today: moment().format('YYYY-MM-DD'),
      nowHour: moment().hour(),
    };
  },

  computed: {
    isProfile() {
      return this.mode === 'profile';
    },

    queueDef() {
      if (this.isProfile) return PROFILE_QUEUE;
      return queueByKey(this.queue) || {};
    },

    canEditResolved() {
      if (this.canEdit === null || this.canEdit === undefined) {
        return !!(this.$can && this.$can('update', 'followupDate'));
      }
      return this.canEdit === true;
    },

    layout() {
      return layoutFor(this.containerWidth);
    },

    remindersPaidFor() {
      const s = this.allSettings && this.allSettings.APPT_REMINDER_PAID_FOR;
      return !!(s && s.PropertyValue === 'TRUE');
    },

    reminderSettings() {
      const s = this.allSettings && this.allSettings.APPT_REMINDER_SETTINGS;
      return parseReminderSettings(s ? s.PropertyValue : null);
    },

    currencySymbol() {
      const s = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      return s && s.PropertyValue ? s.PropertyValue : '$';
    },

    // Same rule as legacy followupNotePlaceholder: only promise the commlog copy when it is on.
    notePlaceholder() {
      const s = this.allSettings && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG;
      return s && s.PropertyValue === 'TRUE'
        ? 'Optional. Add notes here. It will save to commlog...'
        : 'Optional. Add notes here...';
    },

    isRecallQueue() {
      return this.queueDef.key === 'overdueRecall';
    },

    /** One view-model per row, WITHOUT the saving overlay (so a save does not rebuild every row). */
    baseItems() {
      const q = this.queueDef;
      const ctxBase = {
        siteID: this.siteID,
        today: this.today,
        dateFormat: this.dateFormatStr,
        currency: this.currencySymbol,
        categoryOf: statusCategory,
      };
      const remindersOn = this.isRecallQueue && this.remindersPaidFor;
      const now = { date: this.today, hour: this.nowHour };
      if (this.isProfile) {
        const ctxProfile = Object.assign({ patientName: this.patientName, facts: this.profileFacts }, ctxBase);
        return (this.rows || []).map((row) => {
          const rq = this.rowQueue(row);
          const fu = this.resolveFu(row, false);
          const vm = buildProfileRow(rq, row, Object.assign({ fu, reminders: null }, ctxProfile));
          vm.fuBase = fu;
          vm.sub = '';
          vm.assignee = rq.showsAssignee && row.EmployeeManuallyAssigned === 1
            ? (this.getEmployeeName(row.AssignedToEmpID) || (row.EmployeeName ? String(row.EmployeeName) : ''))
            : '';
          vm.listRoute = null;
          return vm;
        });
      }
      return (this.rows || []).map((row) => {
        const fu = resolveFollowupState(row, { today: this.today, canEdit: this.canEditResolved });
        const reminders = remindersOn ? reminderCell(row, this.reminderSettings, now) : null;
        const vm = buildRow(q, row, Object.assign({ fu, reminders }, ctxBase));
        vm.fuBase = fu;
        vm.sub = this.patientSubOf(vm);
        vm.assignee = q.showsAssignee && row.EmployeeManuallyAssigned === 1
          ? this.getEmployeeName(row.AssignedToEmpID)
          : '';
        vm.listRoute = vm.list && vm.list.pageName
          ? { name: vm.list.pageName, params: { listID: vm.list.id } }
          : null;
        return vm;
      });
    },

    /** baseItems with the per-row "Saving…" state laid over the rows that are saving. */
    items() {
      const saving = this.actions.state.saving;
      return this.baseItems.map((it) => {
        if (!it.id || !saving[it.id]) return it;
        const fu = this.resolveFu(it.row, true);
        return Object.assign({}, it, { fu });
      });
    },

    // filterSort reads the PRE-save state, so a saving row keeps its chip and its sort position.
    filterCtx() {
      return { stateOf: it => (it.fuBase ? it.fuBase.state : undefined) };
    },

    viewCounts() {
      return countsByView(this.items, this.filterCtx);
    },

    viewChips() {
      const counts = this.viewCounts;
      return VIEWS.map(v => ({ key: v.key, label: v.label, count: counts[v.key] }));
    },

    /** The chip counts plus the "Due now" split (due today / past due), emitted as `counts`. */
    dueCounts() {
      let dueToday = 0;
      let pastDue = 0;
      this.items.forEach((it) => {
        const s = this.filterCtx.stateOf(it);
        if (s === 'due') dueToday += 1;
        else if (s === 'past') pastDue += 1;
      });
      return Object.assign({}, this.viewCounts, { dueToday, pastDue });
    },

    statusOptions() {
      const seen = {};
      this.items.forEach((it) => {
        if (!it.statusLabel) return;
        if (!seen[it.statusLabel]) {
          seen[it.statusLabel] = { label: it.statusLabel, count: 0, category: it.cells.status.category || 'none' };
        }
        seen[it.statusLabel].count += 1;
      });
      return Object.keys(seen)
        .map(k => seen[k])
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    },

    visibleItems() {
      return visibleRows(this.items, {
        view: this.showChips ? this.currentView : 'all',
        statuses: this.statuses,
        search: this.search,
        sort: this.sort,
      }, this.filterCtx);
    },

    shownItems() {
      const cap = this.maxRows > 0 ? Math.min(this.limit, this.maxRows) : this.limit;
      return this.visibleItems.slice(0, cap);
    },

    hasMore() {
      return this.visibleItems.length > this.shownItems.length;
    },

    showChips() {
      return this.mode !== 'dashboard' && !this.isProfile;
    },

    // The chips + search / filter / sort toolbar. Patient Profile has none (see the header).
    showHead() {
      return !this.isProfile;
    },

    anyFilter() {
      return !!(this.externalFilter || this.search || this.statuses.length || (this.showChips && this.currentView !== 'all'));
    },

    allColumns() {
      return columnsFor(this.queueDef, {
        width: this.containerWidth,
        list: this.mode === 'list',
        remindersPaidFor: this.remindersPaidFor,
      });
    },

    // Selection: only where the queue's column set has a checkbox AND the page owns a selection.
    selectable() {
      return (this.mode === 'page' || this.mode === 'list') && this.allColumns.indexOf('check') !== -1;
    },

    columns() {
      return this.allColumns.filter((c) => {
        if (c === 'check') return this.selectable;
        if (c === 'remove') return this.mode === 'list';
        return true;
      });
    },

    sortOptions() {
      return this.columns
        .filter(c => COLUMN_DEFS[c] && COLUMN_DEFS[c].sortable && COLUMN_DEFS[c].label)
        .map(c => ({ value: c, label: `Sort: ${COLUMN_DEFS[c].label}` }));
    },

    selectedIds() {
      const out = {};
      (this.selected || []).forEach((r) => {
        const id = this.isProfile ? profileRowIdOf(r) : rowIdOf(this.queueDef, r, this.siteID);
        if (id !== null) out[id] = true;
      });
      return out;
    },

    // Legacy isMainChecked compares lengths; this asks the same question row by row.
    allChecked() {
      return this.rows.length > 0 && this.items.every(it => !!this.selectedIds[it.id]);
    },

    someChecked() {
      return !this.allChecked && this.items.some(it => !!this.selectedIds[it.id]);
    },

    showBulkBar() {
      return this.selectable && this.selected.length > 0;
    },

    showAi() {
      return this.isSuperAdmin && this.isRecallQueue && (this.mode === 'page' || this.mode === 'list');
    },

    noun() {
      return NOUNS[this.queueDef.key] || 'rows';
    },

    searchPlaceholder() {
      if (this.mode === 'list') return 'Search this list…';
      return SEARCH_PLACEHOLDERS[this.queueDef.key] || 'Search…';
    },

    popoverItem() {
      if (!this.popover) return null;
      return this.items.find(it => it.id === this.popover.id) || null;
    },

    // The queue the open popover's row belongs to: its own on Patient Profile, else the page's.
    popoverQueue() {
      if (!this.popoverItem) return this.queueDef;
      return this.rowQueue(this.popoverItem.row);
    },

    popoverEmployees() {
      return this.actions.responsibleEmployees(this.popoverItem ? this.popoverItem.row : undefined);
    },

    popoverEmployeeId() {
      if (!this.popoverItem) return '';
      const e = this.actions.preselectedEmployee(this.popoverItem.row);
      return e ? String(e.EmployeeID) : '';
    },
  },

  watch: {
    // A new chip / search / status / sort, or new data, starts the window again.
    currentView() { this.limit = PAGE_SIZE; },
    view(v) { if (v && v !== this.currentView) this.currentView = v; },
    search() { this.limit = PAGE_SIZE; },
    statuses() { this.limit = PAGE_SIZE; },
    sort() { this.limit = PAGE_SIZE; },
    rows() { this.limit = PAGE_SIZE; },
    // The row under an open popover went away (filtered out, reloaded) — close it.
    popoverItem(v) { if (!v && this.popover) this.closePopover(); },
  },

  created() {
    this.actions = createFollowupActions(this);
    this.actions.install();
    // The page's summary card and tab badge read these (the chips' own numbers). Watched from
    // here, not `watch:`, because `items` needs `actions`, which only exists from this point.
    this.$watch('dueCounts', this.emitCounts, { immediate: true });
  },

  mounted() {
    this.measure();
    if (typeof window.ResizeObserver === 'function') {
      // Applied on the NEXT frame (same fix as DrawerLayout, 6c): crossing a breakpoint swaps the
      // column set / card layout, which resizes the observed root inside the callback and raised
      // "ResizeObserver loop completed with undelivered notifications". Last width in a frame wins.
      this.resizeObserver = new window.ResizeObserver((entries) => {
        const e = entries && entries[0];
        const w = e && e.contentRect ? e.contentRect.width : 0;
        if (typeof window.requestAnimationFrame !== 'function') { this.applyWidth(w); return; }
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => { this.resizeFrame = null; this.applyWidth(w); });
      });
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
    // "Due today" must turn over at midnight on a page left open (legacy re-ran moment() on render).
    this.clock = window.setInterval(this.tick, 60000);
  },

  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFrame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.resizeFrame);
    window.removeEventListener('resize', this.measure);
    window.clearInterval(this.clock);
    this.closePopover();
    this.actions.dispose();
  },

  methods: {
    tick() {
      this.today = moment().format('YYYY-MM-DD');
      this.nowHour = moment().hour();
    },

    /** The queue whose rules apply to `row` — its own (by TaskTypeID) on Patient Profile. */
    rowQueue(row) {
      if (!this.isProfile) return this.queueDef;
      return queueByTaskTypeID((row || {}).TaskTypeID) || {};
    },

    /** The row's Follow-up state: Patient Profile's chain in profile mode, AdminTable's elsewhere. */
    resolveFu(row, saving) {
      const ctx = { today: this.today, canEdit: this.canEditResolved };
      if (saving) ctx.saving = true;
      return this.isProfile ? resolveProfileFollowupState(row, ctx) : resolveFollowupState(row, ctx);
    },

    measure() {
      const el = this.$el;
      const w = el ? (el.getBoundingClientRect().width || el.clientWidth) : 0;
      this.applyWidth(w || window.innerWidth);
    },

    // A hidden queue (inactive tab) reports 0 — keep the last real width rather than go "phone".
    applyWidth(w) {
      const n = Math.round(Number(w));
      if (Number.isFinite(n) && n > 0) this.containerWidth = n;
    },

    /** The line under the name: rowModel's age / provider, plus the hygienist on the recall page. */
    patientSubOf(vm) {
      const parts = vm.cells.patient.sub ? [vm.cells.patient.sub] : [];
      if (this.isRecallQueue && this.mode === 'page' && vm.hygienist) parts.push(`Hyg. ${vm.hygienist}`);
      return parts.join(' · ');
    },

    emitCounts(c) {
      const key = JSON.stringify(c);
      if (key === this.lastCountsKey) return;
      this.lastCountsKey = key;
      this.$emit('counts', c);
    },

    setView(key) {
      if (this.currentView === key) return;
      this.currentView = key;
      this.$emit('update:view', key);
    },
    setSearch(v) {
      this.search = v;
    },
    setStatuses(list) {
      this.statuses = list.slice();
    },
    setSortKey(key) {
      this.sort = key ? { key, dir: DESC_FIRST.indexOf(key) !== -1 ? -1 : 1 } : null;
    },
    // Header click: ascending → descending → off (the export's cycle).
    toggleSort(key) {
      if (!this.sort || this.sort.key !== key) this.sort = { key, dir: 1 };
      else if (this.sort.dir === 1) this.sort = { key, dir: -1 };
      else this.sort = null;
    },
    clearFilters() {
      this.search = '';
      this.statuses = [];
      this.setView('all');
      this.$emit('clear-filters');
    },

    onToggleRow(item) {
      this.$emit('toggle-row', item.row);
    },
    onToggleAll() {
      this.$emit('toggle-all', { rows: this.rows, checked: !this.allChecked });
    },

    openDrawer(item) {
      this.closePopover();
      this.actions.openDrawer(item.row);
    },

    openPopover({ item, el }) {
      if (!item || !item.id || !item.fu) return;
      if (!item.fu.editable && !item.fu.canRestart) return;
      if (this.popover && this.popover.id === item.id) {
        this.closePopover();
        return;
      }
      const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
      this.popoverAnchorEl = el || null;
      this.popover = {
        id: item.id,
        rect: r ? { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height } : null,
      };
    },

    closePopover() {
      if (!this.popover) return;
      this.popover = null;
      const el = this.popoverAnchorEl;
      this.popoverAnchorEl = null;
      // Give focus back to the cell that opened it (if it is still on screen).
      if (el && typeof el.focus === 'function' && document.body.contains(el)) {
        this.$nextTick(() => el.focus());
      }
    },

    employeeById(id, row) {
      if (id === '' || id === null || id === undefined) return null;
      return this.actions.responsibleEmployees(row).find(e => String(e.EmployeeID) === String(id)) || null;
    },

    onPopoverSave({ kind, date, note, employeeId }) {
      const item = this.popoverItem;
      if (!item) return;
      let employee = this.employeeById(employeeId, item.row);
      // Patient Profile: an untouched (or hidden) picker keeps the row's own assignment —
      // `undefined` tells followupActions.changeDate so (see its header).
      if (this.isProfile && String(employeeId || '') === this.popoverEmployeeId) employee = undefined;
      this.closePopover();
      if (kind === 'initial') this.actions.setInitialDate(item.row, { date, note, employee });
      else this.actions.changeDate(item.row, { date, employee });
    },

    runDanger(action) {
      const item = this.popoverItem;
      if (!item) return;
      this.closePopover();
      this.actions[action](item.row);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-queue {
  @include fu-font-base;
  position: relative;
  min-width: 0;
}

.fu-queue__card {
  @include fu-card;
}

// Embedded (Patient Profile's Overview card): the host's card is the frame.
.fu-queue--embedded .fu-queue__card {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
}

.fu-queue__head {
  padding: $fu-space-4 18px;
  border-bottom: 1px solid $fu-border;
}

.fu-queue__chips-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $fu-space-2;
}

.fu-queue__switches {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-2;
}

.fu-queue__toolbar--spaced {
  margin-top: 14px;
}

.fu-queue--phone {
  .fu-queue__head { padding: 14px $fu-space-4; }
  // One line: a long switch label wraps inside itself rather than pushing its (i) to a new line.
  .fu-queue__switches { margin-left: 0; flex-wrap: nowrap; max-width: 100%; }
}
</style>
